import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import ResizableRect from 'react-resizable-rotatable-draggable'
//MUI
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
//Redux
import { useSelector } from 'react-redux'

const Image = (props) => {
  const { image, updateImage, deleteImage } = props

  const page = useSelector((state) => state.document.page)

  const [isToolVisible, setIsToolVisible] = useState(false)
  var visibilityProps = useMemo(() => {
    return {
      opacity: isToolVisible ? 1 : 0,
      visibility: isToolVisible ? 'visible' : 'hidden',
    }
  }, [isToolVisible])

  const [prevHeight, setPrevHeight] = useState(100)
  const [prevWidth, setPrevWidth] = useState(100)
  const PIXEL_CHANGE = 3
  const MINIMUM_HEIGHT = 50
  const MINIMUM_WIDTH = 50

  const contentStyle = useMemo(() => {
    return {
      width: image.imgWidth,
      height: image.imgHeight,
      top: image.imgTop,
      left: image.imgLeft,
      position: 'absolute',
      userSelect: 'none',
    }
  }, [image])

  const closeBtnStyle = useMemo(() => {
    return {
      top: image.imgTop + 3,
      left: image.imgLeft + 3,
      width: image.imgWidth > image.imgHeight ? image.imgHeight * 0.2 : image.imgWidth * 0.2,
      height: image.imgHeight > image.imgWidth ? image.imgWidth * 0.2 : image.imgHeight * 0.2,
      minHeight: MINIMUM_HEIGHT / 2,
      minWidth: MINIMUM_WIDTH / 2,
      position: 'absolute',
      userSelect: 'none',
      zIndex: 1,
      cursor: 'pointer',
      backgroundColor: 'red',
      color: 'white',
      borderRadius: '50%',
      ...visibilityProps,
      transition: 'visibility 0s, opacity .35s ease-in-out',
    }
  }, [image, visibilityProps])

  useEffect(() => {
    if (page.pageNumber === image.pageNumber) {
      const newImgWidth = image.imgWidth > page.pageWidth ? page.pageWidth / 2 : image.imgWidth
      const newImgHeight = image.imgHeight > page.pageHeight ? page.pageHeight / 2 : image.imgHeight
      updateImage({
        ...image,
        offsetLeft: page.offsetLeft,
        offsetTop: page.offsetTop,
        multiplier: page.multiplier,
        pageHeight: page.pageHeight,
        pageWidth: page.pageWidth,
        imgWidth: newImgWidth,
        imgHeight: newImgHeight,
        ...(image.imgLeft < page.offsetLeft && {
          imgLeft: page.offsetLeft,
        }),
        ...(image.imgTop < page.offsetTop && {
          imgTop: page.offsetTop,
        }),
        ...(image.imgLeft + image.imgWidth > page.offsetLeft + page.pageWidth && {
          imgLeft: page.offsetLeft + page.pageWidth - newImgWidth,
        }),
        ...(image.imgTop + image.imgHeight > page.offsetTop + page.pageHeight && {
          imgTop: page.offsetTop + page.pageHeight - newImgHeight,
        }),
      })
    }
  }, [page])

  const handleResize = (style, isShiftKey, type) => {
    let { top, left, width, height } = style
    switch (type) {
      case 't': //top handler
        if (contentStyle.top >= image.offsetTop) {
          updateImage({
            ...image,
            imgHeight: Math.round(height),
            imgTop: Math.round(top),
          })
        }
        break
      case 'b': //bottom handler
        if (contentStyle.top + contentStyle.height <= image.offsetTop + image.pageHeight) {
          updateImage({
            ...image,
            imgHeight: Math.round(height),
            imgTop: Math.round(top),
          })
        }
        break
      case 'r': //right handler
        if (contentStyle.left + contentStyle.width <= image.offsetLeft + image.pageWidth) {
          updateImage({
            ...image,
            imgWidth: Math.round(width),
            imgLeft: Math.round(left),
          })
        }
        break
      case 'l': //left handler
        if (contentStyle.left >= image.offsetLeft) {
          updateImage({
            ...image,
            imgWidth: Math.round(width),
            imgLeft: Math.round(left),
          })
        }
        break
      case 'br': //btm right
        if (
          contentStyle.top + contentStyle.height <= image.offsetTop + image.pageHeight &&
          contentStyle.left + contentStyle.width <= image.offsetLeft + image.pageWidth
        ) {
          if (width > prevWidth || height > prevHeight) {
            updateImage({
              ...image,
              imgWidth: Math.round(contentStyle.width + PIXEL_CHANGE),
              imgHeight: Math.round(contentStyle.height + PIXEL_CHANGE),
            })
          } else if (
            (width < prevWidth || height < prevHeight) &&
            contentStyle.width >= MINIMUM_WIDTH &&
            contentStyle.height >= MINIMUM_HEIGHT
          ) {
            updateImage({
              ...image,
              imgWidth: Math.round(contentStyle.width - PIXEL_CHANGE),
              imgHeight: Math.round(contentStyle.height - PIXEL_CHANGE),
            })
          }
          setPrevHeight(height)
          setPrevWidth(width)
        }
        break
      default:
    }
  }

  const handleResizeEnd = () => {
    if (contentStyle.top < Math.round(image.offsetTop)) {
      updateImage({ ...image, imgTop: image.offsetTop })
    } //top
    if (contentStyle.top + contentStyle.height > image.offsetTop + image.pageHeight) {
      //btm
      const newOffset = Math.round(contentStyle.top + contentStyle.height - (image.offsetTop + image.pageHeight))
      updateImage({
        ...image,
        imgTop: contentStyle.top - newOffset,
        imgHeight: contentStyle.height - newOffset,
      })
    }
    if (contentStyle.left < Math.round(image.offsetLeft)) {
      updateImage({ ...image, imgLeft: image.offsetLeft })
    } // left
    if (contentStyle.left + contentStyle.width > image.offsetLeft + image.pageWidth) {
      //right
      const newOffset = Math.round(contentStyle.left + contentStyle.width - (image.offsetLeft + image.pageWidth))
      updateImage({
        ...image,
        imgLeft: contentStyle.left - newOffset,
        imgWidth: contentStyle.width - newOffset,
      })
    }
  }

  const handleDrag = (deltaX, deltaY) => {
    if (
      image.imgLeft + deltaX >= image.offsetLeft &&
      image.imgLeft + deltaX <= image.offsetLeft + image.pageWidth - image.imgWidth && //checking x-axis
      image.imgTop + deltaY >= image.offsetTop &&
      image.imgTop + deltaY <= image.offsetTop + image.pageHeight - image.imgHeight
    ) {
      //checking y-axis
      updateImage({
        ...image,
        imgLeft: image.imgLeft + deltaX,
        imgTop: image.imgTop + deltaY,
      })
    }
  }

  return (
    <div
      onMouseLeave={() => setIsToolVisible(false)}
      onMouseEnter={() => setIsToolVisible(true)}
      style={page.pageNumber === image.pageNumber ? { display: 'inline' } : { display: 'none' }}
    >
      <div
        onClick={() => {
          deleteImage(image.id)
        }}
      >
        <HighlightOffIcon style={closeBtnStyle} />
      </div>
      <>
        <div style={contentStyle}>
          <img src={image.imgUrl} alt='pic' width={image.imgWidth} height={image.imgHeight} />
        </div>
        <ResizableRect
          top={image.imgTop}
          left={image.imgLeft}
          aspectRatio
          minWidth={MINIMUM_WIDTH}
          width={image.imgWidth}
          minHeight={MINIMUM_HEIGHT}
          height={image.imgHeight}
          onDrag={handleDrag}
          onResize={handleResize}
          onResizeEnd={handleResizeEnd}
          zoomable='n,s,e,w,se'
        />
      </>
    </div>
  )
}

Image.propTypes = {
  image: PropTypes.object.isRequired,
  updateImage: PropTypes.func.isRequired,
  deleteImage: PropTypes.func.isRequired,
}

export default Image
